<!--
  SupportRoutingEditor.svelte — who hears about, and who is handed, new tickets
  under one routing rule.

  A rule is a product, optionally narrowed to one category. The product rule
  covers every ticket on the product; a category × product rule *adds* its
  notified agents to the product rule's, and its auto-assign candidates take
  precedence over the product rule's (which remain the fallback). The editor
  states which of the two it is editing, because the same two ticks mean
  something different in each.

  Every agent is listed with two independent flags — Notify and Auto-assign —
  and saving sends the agents carrying at least one; the server drops a member
  with neither anyway. Only agents are offered: the mail links to the Support
  Desk, which nobody else can open, and the server refuses anyone else. A member
  who has since lost the agent role is therefore not carried into the save.

  The flags are kept in a plain record and written with the one-way
  `checked={…}` + `onchange` form: a `bind:checked` on a record entry that has
  never been toggled binds `undefined`, which Svelte 5 refuses mid-update.
-->
<script lang="ts">
	import { Button, Checkbox, Modal, Spinner } from 'flowbite-svelte';
	import { setSupportRoutingRule } from '../AppNetwork';
	import type { SupportPerson, SupportRoutingMemberInput, SupportRoutingRule } from '../AppTypes';
	import { toastError, toastSuccess } from '../toast';
	import { t, t2 } from '../i18n';

	interface Props {
		/** Whether the editor is shown. */
		open?: boolean;
		/** The product the rule belongs to. */
		applicationUUID: string;
		/** What the product is called, for the title and explanation. */
		productName: string;
		/** The category the rule is narrowed to, or `null` for the product rule. */
		categoryUUID?: string | null;
		/** What that category is called; ignored for the product rule. */
		categoryName?: string | null;
		/** The rule as saved, or `null` when it has no members yet. */
		rule: SupportRoutingRule | null;
		/** Every support agent — everyone who can be a member. */
		agents: SupportPerson[];
		/** Called with the rule as the server saved it. */
		onsaved?: (_rule: SupportRoutingRule) => void;
	}

	let {
		open = $bindable(false),
		applicationUUID,
		productName,
		categoryUUID = null,
		categoryName = null,
		rule,
		agents,
		onsaved
	}: Props = $props();

	/** One agent's two ticks. */
	interface Flags {
		/** Told of each new ticket. */
		notify: boolean;
		/** A candidate for automatic assignment. */
		autoAssign: boolean;
	}

	/** The ticks being edited, by agent UUID. An absent agent has neither. */
	let flags: Record<string, Flags> = $state({});
	/** Whether a save is in flight. */
	let saving = $state(false);

	/* Each opening starts from the saved rule, never from an abandoned edit. Reads
	   `open` and `rule` only and writes `flags`, so it cannot re-trigger itself. */
	$effect(() => {
		if (!open) return;
		flags = Object.fromEntries(
			(rule?.members ?? []).map((member) => [
				member.person.userUUID,
				{ notify: member.notify, autoAssign: member.autoAssign }
			])
		);
	});

	/** Whether the rule narrows the product to a category. */
	let isCategoryRule = $derived(categoryUUID !== null);

	/** The modal's title, naming the product and, for a category rule, the category. */
	let title = $derived(
		isCategoryRule
			? $t('Routing for {{category}} on {{product}}', { category: categoryName ?? '', product: productName })
			: $t('Routing for {{product}}', { product: productName })
	);

	/** Sets one flag of one agent, keeping the other. */
	function setFlag(userUUID: string, flag: keyof Flags, on: boolean): void {
		const current = flags[userUUID] ?? { notify: false, autoAssign: false };
		flags = { ...flags, [userUUID]: { ...current, [flag]: on } };
	}

	/** Saves the ticked agents as the rule's members. */
	async function save(): Promise<void> {
		const members: SupportRoutingMemberInput[] = agents
			.map((person) => ({ userUUID: person.userUUID, ...(flags[person.userUUID] ?? { notify: false, autoAssign: false }) }))
			.filter((member) => member.notify || member.autoAssign);
		saving = true;
		/** The saved rule comes back and replaces the caller's copy. */
		try {
			const saved = await setSupportRoutingRule(applicationUUID, categoryUUID, members);
			open = false;
			onsaved?.(saved);
			toastSuccess(t2('Routing saved.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save routing'));
		} finally {
			saving = false;
		}
	}
</script>

<Modal bind:open {title} size="md">
	<p class="text-default mb-3 text-sm">
		{isCategoryRule
			? $t('Applies to {{category}} tickets on {{product}}, in addition to the product rule.', {
					category: categoryName ?? '',
					product: productName
				})
			: $t(
					'Applies to every ticket on {{product}}. Category rules add to it; for assignment, a category rule with auto-assign takes precedence.',
					{ product: productName }
				)}
	</p>
	<p class="text-default mb-3 text-xs">
		<i class="fa-solid fa-bell me-1"></i>{$t('Notify: gets an email and a notification for each new ticket.')}
		<br />
		<i class="fa-solid fa-user-check me-1"></i>{$t('Auto-assign: new tickets go to the least busy of these agents.')}
	</p>
	{#if agents.length === 0}
		<p class="text-default text-sm">{$t('There are no support agents yet. Assign the support role first.')}</p>
	{:else}
		<ul class="divide-y divide-gray-200 dark:divide-gray-700" aria-label={$t('Agents')}>
			{#each agents as person (person.userUUID)}
				{@const current = flags[person.userUUID]}
				<li class="flex flex-wrap items-center gap-x-4 gap-y-1 py-2" data-agent={person.userUUID}>
					<div class="min-w-0 flex-1">
						<p class="font-medium text-gray-900 dark:text-white">{person.name}</p>
						{#if person.email}<p class="text-default text-xs">{person.email}</p>{/if}
					</div>
					<Checkbox
						checked={current?.notify === true}
						onchange={(event: Event) =>
							setFlag(person.userUUID, 'notify', (event.currentTarget as HTMLInputElement).checked)}
						aria-label={$t('Notify {{name}}', { name: person.name })}
					>
						{$t('Notify')}
					</Checkbox>
					<Checkbox
						checked={current?.autoAssign === true}
						onchange={(event: Event) =>
							setFlag(person.userUUID, 'autoAssign', (event.currentTarget as HTMLInputElement).checked)}
						aria-label={$t('Auto-assign to {{name}}', { name: person.name })}
					>
						{$t('Auto-assign')}
					</Checkbox>
				</li>
			{/each}
		</ul>
	{/if}
	{#snippet footer()}
		<div class="flex w-full justify-end gap-2">
			<Button color="alternative" onclick={() => (open = false)}>{$t('Cancel')}</Button>
			<Button disabled={saving} onclick={save}>
				{#if saving}<Spinner size="4" class="me-2" />{/if}{$t('Save')}
			</Button>
		</div>
	{/snippet}
</Modal>
